import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProfessionalModal from '../professionalModal';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import { useProfessionalsList } from '../hooks/useProfessionalsList';
import { useDeleteProfessional } from '../hooks/useDeleteProfessional';
import { useRooms } from '../../../hooks/useRooms';
import { useRoomLabel } from '../../../hooks/useRoomLabel';
import { DigitalCertificateStatus, WorkingHoursSummary } from '../components/professionalDetails';

function ProfessionalList() {
  const navigate = useNavigate();

  const {
    professionals,
    isLoading,
    error: listError,
    refetch: getProfessionals,
  } = useProfessionalsList();
  const { remove } = useDeleteProfessional();
  const { rooms } = useRooms();
  const roomLabel = useRoomLabel();

  const getRoomName = (roomId) => rooms.find((room) => room.id === roomId)?.name ?? '—';

  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, professionalId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProfessionalId(professionalId);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const editProfessional = () => {
    const professionalToEdit = professionals.find((d) => d.id === selectedProfessionalId);
    if (professionalToEdit) {
      setSelectedProfessional(professionalToEdit);
      setModalOpen(true);
    }
    closeMenu();
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedProfessionalId)
      .then((returnFromApi) => {
        getProfessionals();
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('Erro ao excluir o profissional!', returnFromApi);
        if (returnFromApi.response?.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => {
        setSelectedProfessionalId(null);
      });
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedProfessionalId(null);
  };

  const openCreateModal = () => {
    setSelectedProfessional(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedProfessional(null);
  };

  function saveProfessional(returnFromApi) {
    getProfessionals();
    closeModal();
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
  }

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                Profissionais
              </Typography>
              <Button data-cy="new-dentist-btn" variant="outlined" onClick={openCreateModal}>
                Adicionar Profissional
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>WhatsApp</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{roomLabel.singular}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Horários de atendimento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Certificado digital</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {professionals.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        Nenhum profissional cadastrado ainda.
                      </TableCell>
                    </TableRow>
                  )}
                  {professionals.map((professional) => (
                    <TableRow key={professional.id} data-cy="dentist-row" hover>
                      <TableCell>
                        {professional.name}
                        {professional.croNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            CRO-{professional.croUf} {professional.croNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {professional.whatsApp || '—'}
                      </TableCell>
                      <TableCell>{getRoomName(professional.roomId)}</TableCell>
                      <TableCell>
                        <WorkingHoursSummary workingHours={professional.workingHours} />
                      </TableCell>
                      <TableCell>
                        <DigitalCertificateStatus
                          fileName={professional.digitalCertificateFileName}
                          uploadedAt={professional.digitalCertificateUploadedAt}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          data-cy="dentist-actions-btn"
                          aria-label="ações"
                          onClick={(event) => clickMenu(event, professional.id)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={editProfessional}>Editar Profissional</MenuItem>
          <MenuItem
            data-cy="delete-dentist-btn"
            onClick={openConfirmModal}
            sx={{ color: 'error.main' }}
          >
            Excluir
          </MenuItem>
        </Menu>

        <ProfessionalModal
          open={isModalOpen}
          onClose={closeModal}
          onSave={saveProfessional}
          professional={selectedProfessional}
        />

        <Dialog
          open={isConfirmModalOpen}
          onClose={closeConfirmModal}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold' }}>
            {'Confirmar Exclusão'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Você tem certeza que deseja excluir este profissional? Essa ação não pode ser
              desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmModal}>Cancelar</Button>
            <Button
              data-cy="confirm-delete-dentist-btn"
              onClick={confirmDelete}
              variant="contained"
              color="error"
              autoFocus
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default ProfessionalList;
